import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogActions,
  MatDialogContent,
  MatDialogTitle,
} from '@angular/material/dialog';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { InvitationService } from '../../../core/entity/invitation.service';
import { InvitableRole, Invitation } from '../../../core/entity/invitation.types';

export interface InviteDialogData {
  readonly shortId: string;
  readonly entityName: string;
}

@Component({
  selector: 'app-invite-dialog',
  imports: [
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    ReactiveFormsModule,
  ],
  templateUrl: './invite-dialog.html',
  styleUrl: './invite-dialog.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InviteDialogComponent {
  protected readonly data = inject<InviteDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef = inject(MatDialogRef<InviteDialogComponent, Invitation[]>);
  private readonly invitations = inject(InvitationService);
  private readonly fb = inject(FormBuilder);

  protected readonly isSaving = signal(false);
  protected readonly serverError = signal<string | null>(null);

  protected readonly ROLES: { value: InvitableRole; label: string; hint: string }[] = [
    {
      value: 'entity-admin',
      label: 'Owner / Admin',
      hint: 'Can manage the business, its team and its applications.',
    },
    {
      value: 'entity-member',
      label: 'Team member',
      hint: 'Can view and prepare applications.',
    },
  ];

  protected readonly form = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
    role: ['entity-admin' as InvitableRole, Validators.required],
    message: ['', Validators.maxLength(1000)],
  });

  protected get roleHint(): string {
    return this.ROLES.find((r) => r.value === this.form.controls.role.value)?.hint ?? '';
  }

  protected submit(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid || this.isSaving()) return;

    this.serverError.set(null);
    this.isSaving.set(true);
    const v = this.form.getRawValue();

    this.invitations
      .create(this.data.shortId, {
        email: v.email!,
        role: v.role!,
        ...(v.message ? { message: v.message } : {}),
      })
      .subscribe({
        next: (res) => this.dialogRef.close(res),
        error: (err: HttpErrorResponse) => {
          this.isSaving.set(false);
          if (err.status === 409 && err.error?.detail) {
            this.serverError.set(err.error.detail as string);
          } else if (err.status === 400 && err.error?.detail?.email) {
            this.form.controls.email.setErrors({
              serverError: (err.error.detail.email as string[])[0],
            });
          } else {
            this.serverError.set('Could not send the invitation. Please try again.');
          }
        },
      });
  }

  protected cancel(): void {
    this.dialogRef.close();
  }
}
